/**
 * Route Keys for Navigation
 *
 * Issue #356: Persona Guide Integration with Navigation-Aware Retrieval
 *
 * Stable identifiers for navigation routes that don't change when URLs change.
 * These keys are used to:
 * - Index persona guides with navigation metadata
 * - Resolve current URLs at runtime
 * - Link AI responses to relevant pages
 */

import type { NavPortalType } from './types';

// ============================================================================
// Route Key Types
// ============================================================================

/**
 * All route keys organized by portal
 *
 * Format: portal.section.page or portal.page
 */
export type RouteKey =
  // ============ Admin Portal ============
  | 'admin.dashboard'
  | 'admin.tenants'
  | 'admin.tenants.detail'
  | 'admin.realms'
  | 'admin.realms.detail'
  | 'admin.businesses'
  | 'admin.businesses.detail'
  | 'admin.users'
  | 'admin.users.detail'
  | 'admin.billing'
  | 'admin.billing.subscriptions'
  | 'admin.billing.invoices'
  | 'admin.system.health'
  | 'admin.system.logs'
  | 'admin.settings'

  // ============ Business Portal ============
  // Dashboard
  | 'business.dashboard'
  // Prospecting (merged Pipeline + Campaigns)
  | 'business.prospecting'
  | 'business.prospecting.discovery'
  | 'business.prospecting.leads'
  | 'business.prospecting.leads.detail'
  | 'business.prospecting.sequences'
  | 'business.prospecting.email'
  | 'business.prospecting.email.detail'
  | 'business.prospecting.voice'
  | 'business.prospecting.voice.detail'
  // Loans (was Origination)
  | 'business.loans'
  | 'business.loans.applications'
  | 'business.loans.borrowers'
  | 'business.loans.borrowers.detail'
  | 'business.loans.term-sheets'
  | 'business.loans.term-sheets.detail'
  | 'business.loans.cases'
  | 'business.loans.cases.detail'
  // Partners
  | 'business.partners'
  | 'business.partners.detail'
  // Team
  | 'business.team'
  | 'business.team.members'
  | 'business.team.members.detail'
  | 'business.team.teams'
  | 'business.team.org-chart'
  // Astor AI
  | 'business.astor'
  | 'business.astor.chat'
  | 'business.astor.history'
  // Partnerships
  | 'business.partnerships'
  | 'business.partnerships.referrals'
  | 'business.partnerships.tracking'
  | 'business.partnerships.agreements'
  | 'business.partnerships.commissions'
  // Settings
  | 'business.settings'
  | 'business.settings.branches'
  | 'business.settings.roles'
  | 'business.settings.compliance'
  | 'business.settings.onboarding'
  | 'business.settings.pricing'
  | 'business.settings.pipelines'
  | 'business.settings.workflows'
  | 'business.settings.templates'
  | 'business.settings.enrichment'
  | 'business.settings.integrations'
  | 'business.settings.audit'
  | 'business.settings.profile'
  | 'business.settings.devices'
  | 'business.settings.ai-agents'
  | 'business.settings.ai-autonomy'

  // ============ Consumer Portal ============
  | 'consumer.dashboard'
  | 'consumer.applications'
  | 'consumer.applications.detail'
  | 'consumer.documents'
  | 'consumer.documents.upload'
  | 'consumer.profile'
  | 'consumer.settings'

  // ============ Auth Portal ============
  | 'auth.login'
  | 'auth.register'
  | 'auth.forgot-password'
  | 'auth.reset-password'
  | 'auth.mfa'
  | 'auth.verify-email';

/**
 * Route definition with metadata
 */
export interface RouteDefinition {
  /** Stable route key identifier */
  key: RouteKey;

  /** Portal this route belongs to */
  portal: NavPortalType;

  /** Current URL path (may change over time) */
  path: string;

  /** Human-readable title */
  title: string;

  /** Description for search/help */
  description?: string;

  /** Keywords for semantic search */
  keywords?: string[];

  /** Parent route key (for breadcrumbs) */
  parent?: RouteKey;

  /** Whether this is a detail page requiring an ID */
  requiresId?: boolean;

  /** Permission required to access this route */
  permission?: string;

  /** Capability required to access this route */
  capability?: string;
}

// ============================================================================
// Route Registry
// ============================================================================

/**
 * Complete route registry mapping keys to definitions
 *
 * This is the source of truth for all navigable routes in the platform.
 */
export const ROUTE_REGISTRY: Record<RouteKey, RouteDefinition> = {
  // ============ Admin Portal ============
  'admin.dashboard': {
    key: 'admin.dashboard',
    portal: 'admin',
    path: '/dashboard',
    title: 'Dashboard',
    description: 'Platform-wide metrics and overview',
    keywords: ['dashboard', 'metrics', 'overview', 'stats'],
  },
  'admin.tenants': {
    key: 'admin.tenants',
    portal: 'admin',
    path: '/tenants',
    title: 'Tenants',
    description: 'Manage platform tenants',
    keywords: ['tenants', 'organizations', 'customers'],
  },
  'admin.tenants.detail': {
    key: 'admin.tenants.detail',
    portal: 'admin',
    path: '/tenants/:id',
    title: 'Tenant Details',
    parent: 'admin.tenants',
    requiresId: true,
  },
  'admin.realms': {
    key: 'admin.realms',
    portal: 'admin',
    path: '/realms',
    title: 'Realms',
    description: 'Manage tenant realms',
    keywords: ['realms', 'environments', 'isolation'],
  },
  'admin.realms.detail': {
    key: 'admin.realms.detail',
    portal: 'admin',
    path: '/realms/:id',
    title: 'Realm Details',
    parent: 'admin.realms',
    requiresId: true,
  },
  'admin.businesses': {
    key: 'admin.businesses',
    portal: 'admin',
    path: '/businesses',
    title: 'Businesses',
    description: 'Manage businesses across tenants',
    keywords: ['businesses', 'companies', 'organizations'],
  },
  'admin.businesses.detail': {
    key: 'admin.businesses.detail',
    portal: 'admin',
    path: '/businesses/:id',
    title: 'Business Details',
    parent: 'admin.businesses',
    requiresId: true,
  },
  'admin.users': {
    key: 'admin.users',
    portal: 'admin',
    path: '/users',
    title: 'Users',
    description: 'Platform user management',
    keywords: ['users', 'accounts', 'people'],
  },
  'admin.users.detail': {
    key: 'admin.users.detail',
    portal: 'admin',
    path: '/users/:id',
    title: 'User Details',
    parent: 'admin.users',
    requiresId: true,
  },
  'admin.billing': {
    key: 'admin.billing',
    portal: 'admin',
    path: '/billing',
    title: 'Billing',
    description: 'Platform billing and subscriptions',
    keywords: ['billing', 'subscriptions', 'payments'],
  },
  'admin.billing.subscriptions': {
    key: 'admin.billing.subscriptions',
    portal: 'admin',
    path: '/billing/subscriptions',
    title: 'Subscriptions',
    parent: 'admin.billing',
    keywords: ['subscriptions', 'plans', 'tiers'],
  },
  'admin.billing.invoices': {
    key: 'admin.billing.invoices',
    portal: 'admin',
    path: '/billing/invoices',
    title: 'Invoices',
    parent: 'admin.billing',
    keywords: ['invoices', 'payments', 'receipts'],
  },
  'admin.system.health': {
    key: 'admin.system.health',
    portal: 'admin',
    path: '/system/health',
    title: 'System Health',
    description: 'Service health monitoring',
    keywords: ['health', 'status', 'monitoring', 'uptime'],
  },
  'admin.system.logs': {
    key: 'admin.system.logs',
    portal: 'admin',
    path: '/system/logs',
    title: 'System Logs',
    description: 'Platform-wide logs',
    keywords: ['logs', 'audit', 'events'],
  },
  'admin.settings': {
    key: 'admin.settings',
    portal: 'admin',
    path: '/settings',
    title: 'Settings',
    description: 'Platform settings',
    keywords: ['settings', 'configuration', 'preferences'],
  },

  // ============ Business Portal ============
  'business.dashboard': {
    key: 'business.dashboard',
    portal: 'business',
    path: '/dashboard',
    title: 'Dashboard',
    description: 'Business metrics and overview',
    keywords: ['dashboard', 'metrics', 'overview', 'home'],
  },
  'business.prospecting': {
    key: 'business.prospecting',
    portal: 'business',
    path: '/prospecting',
    title: 'Prospecting',
    description: 'Lead generation, outreach, and campaigns',
    keywords: ['prospecting', 'pipeline', 'leads', 'deals', 'crm', 'sales', 'campaigns'],
  },
  'business.prospecting.discovery': {
    key: 'business.prospecting.discovery',
    portal: 'business',
    path: '/prospecting/discovery',
    title: 'Discovery',
    parent: 'business.prospecting',
    keywords: ['discovery', 'leads', 'prospects'],
  },
  'business.prospecting.leads': {
    key: 'business.prospecting.leads',
    portal: 'business',
    path: '/prospecting/leads',
    title: 'Leads',
    parent: 'business.prospecting',
    keywords: ['leads', 'prospects', 'crm'],
  },
  'business.prospecting.leads.detail': {
    key: 'business.prospecting.leads.detail',
    portal: 'business',
    path: '/prospecting/leads/:id',
    title: 'Lead Details',
    parent: 'business.prospecting.leads',
    requiresId: true,
  },
  'business.prospecting.sequences': {
    key: 'business.prospecting.sequences',
    portal: 'business',
    path: '/prospecting/sequences',
    title: 'Sequences',
    parent: 'business.prospecting',
    keywords: ['sequences', 'automation', 'drip'],
  },
  'business.prospecting.email': {
    key: 'business.prospecting.email',
    portal: 'business',
    path: '/prospecting/email',
    title: 'Email Campaigns',
    parent: 'business.prospecting',
    keywords: ['email', 'campaigns', 'outbound', 'marketing'],
  },
  'business.prospecting.email.detail': {
    key: 'business.prospecting.email.detail',
    portal: 'business',
    path: '/prospecting/email/:id',
    title: 'Email Campaign Details',
    parent: 'business.prospecting.email',
    requiresId: true,
  },
  'business.prospecting.voice': {
    key: 'business.prospecting.voice',
    portal: 'business',
    path: '/prospecting/voice',
    title: 'Voice Campaigns',
    parent: 'business.prospecting',
    keywords: ['voice', 'campaigns', 'calls', 'outbound'],
  },
  'business.prospecting.voice.detail': {
    key: 'business.prospecting.voice.detail',
    portal: 'business',
    path: '/prospecting/voice/:id',
    title: 'Voice Campaign Details',
    parent: 'business.prospecting.voice',
    requiresId: true,
  },
  'business.loans': {
    key: 'business.loans',
    portal: 'business',
    path: '/loans',
    title: 'Loans',
    description: 'Loan origination and management',
    keywords: ['loans', 'origination', 'borrowers', 'cases', 'applications'],
  },
  'business.loans.applications': {
    key: 'business.loans.applications',
    portal: 'business',
    path: '/loans/applications',
    title: 'Applications',
    parent: 'business.loans',
    keywords: ['applications', 'intake', 'submissions'],
  },
  'business.loans.borrowers': {
    key: 'business.loans.borrowers',
    portal: 'business',
    path: '/loans/borrowers',
    title: 'Borrowers',
    parent: 'business.loans',
    keywords: ['borrowers', 'consumers', 'applicants'],
  },
  'business.loans.borrowers.detail': {
    key: 'business.loans.borrowers.detail',
    portal: 'business',
    path: '/loans/borrowers/:id',
    title: 'Borrower Details',
    parent: 'business.loans.borrowers',
    requiresId: true,
  },
  'business.loans.term-sheets': {
    key: 'business.loans.term-sheets',
    portal: 'business',
    path: '/loans/term-sheets',
    title: 'Term Sheets',
    parent: 'business.loans',
    keywords: ['term sheets', 'quotes', 'proposals'],
  },
  'business.loans.term-sheets.detail': {
    key: 'business.loans.term-sheets.detail',
    portal: 'business',
    path: '/loans/term-sheets/:id',
    title: 'Term Sheet Details',
    parent: 'business.loans.term-sheets',
    requiresId: true,
  },
  'business.loans.cases': {
    key: 'business.loans.cases',
    portal: 'business',
    path: '/loans',
    title: 'Cases',
    parent: 'business.loans',
    keywords: ['cases', 'loans', 'applications'],
  },
  'business.loans.cases.detail': {
    key: 'business.loans.cases.detail',
    portal: 'business',
    path: '/loans/:id',
    title: 'Case Details',
    parent: 'business.loans.cases',
    requiresId: true,
  },
  'business.partners': {
    key: 'business.partners',
    portal: 'business',
    path: '/partners',
    title: 'Partners',
    description: 'B2B partner management',
    keywords: ['partners', 'referrals', 'b2b', 'affiliates'],
  },
  'business.partners.detail': {
    key: 'business.partners.detail',
    portal: 'business',
    path: '/partners/:id',
    title: 'Partner Details',
    parent: 'business.partners',
    requiresId: true,
  },
  'business.team': {
    key: 'business.team',
    portal: 'business',
    path: '/team/members',
    title: 'Team',
    description: 'Team management',
    keywords: ['team', 'members', 'employees', 'staff'],
  },
  'business.team.members': {
    key: 'business.team.members',
    portal: 'business',
    path: '/team/members',
    title: 'Team Members',
    parent: 'business.team',
    keywords: ['members', 'employees', 'users'],
  },
  'business.team.members.detail': {
    key: 'business.team.members.detail',
    portal: 'business',
    path: '/team/members/:id',
    title: 'Member Details',
    parent: 'business.team.members',
    requiresId: true,
  },
  'business.team.teams': {
    key: 'business.team.teams',
    portal: 'business',
    path: '/team/teams',
    title: 'Teams',
    parent: 'business.team',
    keywords: ['teams', 'groups', 'departments'],
  },
  'business.team.org-chart': {
    key: 'business.team.org-chart',
    portal: 'business',
    path: '/team/org-chart',
    title: 'Org Chart',
    parent: 'business.team',
    keywords: ['org chart', 'organization', 'hierarchy'],
  },
  'business.astor': {
    key: 'business.astor',
    portal: 'business',
    path: '/astor',
    title: 'Astor AI',
    description: 'AI assistant',
    keywords: ['astor', 'ai', 'assistant', 'chat', 'help'],
  },
  'business.astor.chat': {
    key: 'business.astor.chat',
    portal: 'business',
    path: '/astor/chat',
    title: 'Chat',
    parent: 'business.astor',
    keywords: ['chat', 'conversation', 'ai'],
  },
  'business.astor.history': {
    key: 'business.astor.history',
    portal: 'business',
    path: '/astor/history',
    title: 'Chat History',
    parent: 'business.astor',
    keywords: ['history', 'conversations', 'past'],
  },
  'business.partnerships': {
    key: 'business.partnerships',
    portal: 'business',
    path: '/partnerships',
    title: 'Partnerships',
    description: 'B2B partnership management',
    keywords: ['partnerships', 'referrals', 'agreements'],
    capability: 'canBroker',
  },
  'business.partnerships.referrals': {
    key: 'business.partnerships.referrals',
    portal: 'business',
    path: '/partnerships/referrals',
    title: 'Referrals',
    parent: 'business.partnerships',
    keywords: ['referrals', 'inbound', 'outbound'],
    capability: 'canBroker',
  },
  'business.partnerships.tracking': {
    key: 'business.partnerships.tracking',
    portal: 'business',
    path: '/partnerships/referral-tracking',
    title: 'Referral Tracking',
    parent: 'business.partnerships',
    keywords: ['tracking', 'analytics', 'referrals'],
    capability: 'canBroker',
  },
  'business.partnerships.agreements': {
    key: 'business.partnerships.agreements',
    portal: 'business',
    path: '/partnerships/agreements',
    title: 'Agreements',
    parent: 'business.partnerships',
    keywords: ['agreements', 'contracts', 'terms'],
    capability: 'canBroker',
  },
  'business.partnerships.commissions': {
    key: 'business.partnerships.commissions',
    portal: 'business',
    path: '/partnerships/commissions',
    title: 'Commissions',
    parent: 'business.partnerships',
    keywords: ['commissions', 'payments', 'fees'],
    capability: 'canBroker',
  },
  'business.settings': {
    key: 'business.settings',
    portal: 'business',
    path: '/control-center',
    title: 'Control Center',
    description: 'Business control center hub',
    keywords: ['settings', 'configuration', 'preferences'],
  },
  'business.settings.branches': {
    key: 'business.settings.branches',
    portal: 'business',
    path: '/control-center/branches',
    title: 'Branches',
    parent: 'business.settings',
    keywords: ['branches', 'locations', 'offices'],
  },
  'business.settings.roles': {
    key: 'business.settings.roles',
    portal: 'business',
    path: '/control-center/roles',
    title: 'Custom Roles',
    parent: 'business.settings',
    keywords: ['roles', 'permissions', 'access'],
  },
  'business.settings.compliance': {
    key: 'business.settings.compliance',
    portal: 'business',
    path: '/control-center/compliance',
    title: 'Compliance',
    parent: 'business.settings',
    keywords: ['compliance', 'licenses', 'nmls'],
  },
  'business.settings.onboarding': {
    key: 'business.settings.onboarding',
    portal: 'business',
    path: '/control-center/onboarding-wizard',
    title: 'Onboarding',
    parent: 'business.settings',
    keywords: ['onboarding', 'getting started', 'setup'],
  },
  'business.settings.pricing': {
    key: 'business.settings.pricing',
    portal: 'business',
    path: '/control-center/pricing',
    title: 'Pricing',
    parent: 'business.settings',
    keywords: ['pricing', 'rates', 'fees'],
  },
  'business.settings.pipelines': {
    key: 'business.settings.pipelines',
    portal: 'business',
    path: '/control-center/pipeline',
    title: 'Pipelines',
    parent: 'business.settings',
    keywords: ['pipelines', 'stages', 'workflows'],
  },
  'business.settings.workflows': {
    key: 'business.settings.workflows',
    portal: 'business',
    path: '/control-center/workflows',
    title: 'Workflows',
    parent: 'business.settings',
    keywords: ['workflows', 'automation', 'rules'],
  },
  'business.settings.templates': {
    key: 'business.settings.templates',
    portal: 'business',
    path: '/control-center/templates',
    title: 'Templates',
    parent: 'business.settings',
    keywords: ['templates', 'documents', 'forms'],
  },
  'business.settings.enrichment': {
    key: 'business.settings.enrichment',
    portal: 'business',
    path: '/control-center/enrichment',
    title: 'Data Enrichment',
    parent: 'business.settings',
    keywords: ['enrichment', 'data', 'third-party'],
  },
  'business.settings.integrations': {
    key: 'business.settings.integrations',
    portal: 'business',
    path: '/control-center/integrations',
    title: 'Integrations',
    parent: 'business.settings',
    keywords: ['integrations', 'apps', 'connections'],
  },
  'business.settings.audit': {
    key: 'business.settings.audit',
    portal: 'business',
    path: '/control-center/audit',
    title: 'Audit Logs',
    parent: 'business.settings',
    keywords: ['audit', 'logs', 'history'],
  },
  'business.settings.profile': {
    key: 'business.settings.profile',
    portal: 'business',
    path: '/control-center/business-profile',
    title: 'Business Profile',
    parent: 'business.settings',
    keywords: ['profile', 'company', 'info'],
  },
  'business.settings.devices': {
    key: 'business.settings.devices',
    portal: 'business',
    path: '/control-center/devices',
    title: 'My Devices',
    parent: 'business.settings',
    keywords: ['devices', 'sessions', 'security'],
  },
  'business.settings.ai-agents': {
    key: 'business.settings.ai-agents',
    portal: 'business',
    path: '/control-center/astor-agents',
    title: 'AI Agents',
    parent: 'business.settings',
    keywords: ['agents', 'ai', 'bots'],
  },
  'business.settings.ai-autonomy': {
    key: 'business.settings.ai-autonomy',
    portal: 'business',
    path: '/control-center/astor/autonomy',
    title: 'AI Autonomy',
    parent: 'business.settings',
    keywords: ['autonomy', 'ai', 'permissions'],
  },

  // ============ Consumer Portal ============
  'consumer.dashboard': {
    key: 'consumer.dashboard',
    portal: 'consumer',
    path: '/dashboard',
    title: 'Dashboard',
    description: 'Your loan overview',
    keywords: ['dashboard', 'home', 'overview'],
  },
  'consumer.applications': {
    key: 'consumer.applications',
    portal: 'consumer',
    path: '/applications',
    title: 'Applications',
    description: 'Your loan applications',
    keywords: ['applications', 'loans', 'status'],
  },
  'consumer.applications.detail': {
    key: 'consumer.applications.detail',
    portal: 'consumer',
    path: '/applications/:id',
    title: 'Application Details',
    parent: 'consumer.applications',
    requiresId: true,
  },
  'consumer.documents': {
    key: 'consumer.documents',
    portal: 'consumer',
    path: '/documents',
    title: 'Documents',
    description: 'Your documents',
    keywords: ['documents', 'files', 'uploads'],
  },
  'consumer.documents.upload': {
    key: 'consumer.documents.upload',
    portal: 'consumer',
    path: '/documents/upload',
    title: 'Upload Documents',
    parent: 'consumer.documents',
    keywords: ['upload', 'files', 'submit'],
  },
  'consumer.profile': {
    key: 'consumer.profile',
    portal: 'consumer',
    path: '/profile',
    title: 'Profile',
    description: 'Your profile information',
    keywords: ['profile', 'account', 'info'],
  },
  'consumer.settings': {
    key: 'consumer.settings',
    portal: 'consumer',
    path: '/settings',
    title: 'Settings',
    description: 'Account settings',
    keywords: ['settings', 'preferences', 'account'],
  },

  // ============ Auth Portal ============
  'auth.login': {
    key: 'auth.login',
    portal: 'auth',
    path: '/login',
    title: 'Login',
    keywords: ['login', 'sign in', 'auth'],
  },
  'auth.register': {
    key: 'auth.register',
    portal: 'auth',
    path: '/register',
    title: 'Register',
    keywords: ['register', 'sign up', 'create account'],
  },
  'auth.forgot-password': {
    key: 'auth.forgot-password',
    portal: 'auth',
    path: '/forgot-password',
    title: 'Forgot Password',
    keywords: ['forgot', 'password', 'reset'],
  },
  'auth.reset-password': {
    key: 'auth.reset-password',
    portal: 'auth',
    path: '/reset-password',
    title: 'Reset Password',
    keywords: ['reset', 'password', 'new'],
  },
  'auth.mfa': {
    key: 'auth.mfa',
    portal: 'auth',
    path: '/mfa',
    title: 'MFA Verification',
    keywords: ['mfa', '2fa', 'verification'],
  },
  'auth.verify-email': {
    key: 'auth.verify-email',
    portal: 'auth',
    path: '/verify-email',
    title: 'Verify Email',
    keywords: ['verify', 'email', 'confirmation'],
  },
};

// ============================================================================
// Helper Types
// ============================================================================

/**
 * Get all route keys for a specific portal
 */
export type PortalRouteKeys<P extends NavPortalType> = Extract<RouteKey, `${P}.${string}`>;

/**
 * Route key for admin portal
 */
export type AdminRouteKey = PortalRouteKeys<'admin'>;

/**
 * Route key for business portal
 */
export type BusinessRouteKey = PortalRouteKeys<'business'>;

/**
 * Route key for consumer portal
 */
export type ConsumerRouteKey = PortalRouteKeys<'consumer'>;

/**
 * Route key for auth portal
 */
export type AuthRouteKey = PortalRouteKeys<'auth'>;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get route definition by key
 */
export function getRouteDefinition(key: RouteKey): RouteDefinition | undefined {
  return ROUTE_REGISTRY[key];
}

/**
 * Get all routes for a portal
 */
export function getPortalRoutes(portal: NavPortalType): RouteDefinition[] {
  return Object.values(ROUTE_REGISTRY).filter((route) => route.portal === portal);
}

/**
 * Get child routes for a parent route
 */
export function getChildRoutes(parentKey: RouteKey): RouteDefinition[] {
  return Object.values(ROUTE_REGISTRY).filter((route) => route.parent === parentKey);
}

/**
 * Get breadcrumb trail for a route
 */
export function getBreadcrumbs(key: RouteKey): RouteDefinition[] {
  const breadcrumbs: RouteDefinition[] = [];
  let current: RouteDefinition | undefined = ROUTE_REGISTRY[key];

  while (current) {
    breadcrumbs.unshift(current);
    current = current.parent ? ROUTE_REGISTRY[current.parent] : undefined;
  }

  return breadcrumbs;
}

/**
 * Search routes by keyword
 */
export function searchRoutes(query: string, portal?: NavPortalType): RouteDefinition[] {
  const queryLower = query.toLowerCase();
  return Object.values(ROUTE_REGISTRY).filter((route) => {
    if (portal && route.portal !== portal) return false;

    // Search in title, description, and keywords
    const searchable = [route.title, route.description, ...(route.keywords || [])]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return searchable.includes(queryLower);
  });
}
